import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextInput } from './ui.tsx';

export interface PickerChoice {
  id: string;
  name: string;
  detail?: string;
  disabled?: boolean;
}

/** Selection belongs to the caller: searching or paging never edits its draft. */
export function CheckboxPicker({
  label,
  choices,
  value,
  onChange,
  disabled,
  maxSelected,
  lead = false,
  searchLabel,
}: {
  label: string;
  choices: PickerChoice[];
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  maxSelected?: number;
  lead?: boolean;
  searchLabel?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const matching = choices.filter((choice) =>
    `${choice.name} ${choice.detail ?? ''}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  const pageCount = Math.max(1, Math.ceil(matching.length / 10));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = matching.slice(currentPage * 10, currentPage * 10 + 10);
  function toggle(choiceId: string) {
    onChange(value.includes(choiceId) ? value.filter((entry) => entry !== choiceId) : [...value, choiceId]);
  }
  return (
    <fieldset className="mb-4 min-w-0" aria-labelledby={`${id}-label`}>
      <legend id={`${id}-label`} className="field-label">
        {label}
      </legend>
      <TextInput
        label={searchLabel ?? t('employeePicker.search')}
        type="search"
        value={search}
        disabled={disabled}
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(0);
        }}
      />
      <div className="mb-3 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-brand-soft)] p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <strong role="status">{t('employeePicker.selected', { count: value.length })}</strong>
          <Button variant="ghost" disabled={disabled || value.length === 0} onClick={() => onChange([])}>
            {t('employeePicker.clear')}
          </Button>
        </div>
        {value.length > 0 ? (
          <ul className="max-h-48 overflow-y-auto" aria-label={t('employeePicker.selectedList')}>
            {value.map((choiceId, index) => {
              const choice = choices.find((entry) => entry.id === choiceId);
              const name = choice?.name ?? t('employeePicker.unavailable');
              return (
                <li
                  key={choiceId}
                  className="flex min-w-0 items-center justify-between gap-3 border-t border-[color:var(--color-line)] py-1"
                >
                  <span className="min-w-0 break-words">
                    {name}
                    {lead && index === 0 ? (
                      <span className="ml-2 text-sm font-semibold">{t('employeePicker.lead')}</span>
                    ) : null}
                  </span>
                  <Button
                    variant="ghost"
                    disabled={disabled}
                    aria-label={t('employeePicker.remove', { name })}
                    onClick={() => toggle(choiceId)}
                  >
                    ×
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      {maxSelected !== undefined ? (
        <p className="field-hint mb-2">{t('employeePicker.limit', { count: maxSelected })}</p>
      ) : null}
      <div className="max-h-80 overflow-y-auto rounded-md border border-[color:var(--color-line)]">
        {visible.length === 0 ? (
          <p className="p-3 text-[color:var(--color-muted)]">{t('employeePicker.noMatches')}</p>
        ) : (
          visible.map((choice) => {
            const checked = value.includes(choice.id);
            return (
              <label
                key={choice.id}
                className="flex min-h-12 cursor-pointer items-center gap-3 border-b border-[color:var(--color-line)] p-3 last:border-b-0"
              >
                <input
                  type="checkbox"
                  className="h-5 w-5 shrink-0 accent-[color:var(--color-brand)]"
                  checked={checked}
                  aria-label={choice.name}
                  disabled={
                    disabled ||
                    (!checked && (choice.disabled || (maxSelected !== undefined && value.length >= maxSelected)))
                  }
                  onChange={() => toggle(choice.id)}
                />
                <span className="min-w-0 break-words">
                  <span className="font-medium">{choice.name}</span>
                  {choice.detail ? (
                    <span className="block text-sm text-[color:var(--color-muted)]">{choice.detail}</span>
                  ) : null}
                </span>
              </label>
            );
          })
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <Button disabled={disabled || currentPage === 0} onClick={() => setPage(currentPage - 1)}>
          {t('employeePicker.previous')}
        </Button>
        <span className="text-sm" role="status">
          {t('employeePicker.page', { page: currentPage + 1, pages: pageCount, count: matching.length })}
        </span>
        <Button disabled={disabled || currentPage + 1 >= pageCount} onClick={() => setPage(currentPage + 1)}>
          {t('employeePicker.next')}
        </Button>
      </div>
    </fieldset>
  );
}
